import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import { TaskStatus, TaskPriority, TaskType } from '../../types/common';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import { useBoardMembers } from '../../hooks/useBoardMembers';
import { createTask } from '../../store/taskSlice';
import { showToast } from '../../store/uiSlice';

interface CreateTaskModalProps {
  open: boolean;
  onClose: () => void;
  boardId: string;
  defaultStatus?: TaskStatus;
  /** Sprint the task should be created into, e.g. the board's active sprint. */
  defaultSprintId?: string;
  /** When provided, shows an Epic select populated from this list. */
  epics?: { id: string; name: string }[];
}

const CreateTaskModal: React.FC<CreateTaskModalProps> = ({
  open,
  onClose,
  boardId,
  defaultStatus = TaskStatus.TODO,
  defaultSprintId,
  epics = [],
}) => {
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.tasks);
  const { members: boardMembers, loading: loadingMembers } = useBoardMembers(boardId);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: defaultStatus,
    priority: TaskPriority.MEDIUM,
    type: TaskType.FEATURE,
    assigneeId: '',
    epicId: '',
    dueDate: '',
    estimatedHours: '',
    storyPoints: '',
    tags: [] as string[],
  });

  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState({
    title: '',
    assigneeId: '',
    boardId: '',
    general: '',
  });

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear errors for the field being edited
    if (errors[field as keyof typeof errors]) {
      setErrors((prev) => ({ ...prev, [field]: '', general: '' }));
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags.filter((tag) => tag !== tagToRemove),
    }));
  };

  const validateForm = () => {
    const newErrors = {
      title: '',
      assigneeId: '',
      boardId: '',
      general: '',
    };

    if (!formData.title.trim()) {
      newErrors.title = 'Task title is required';
    } else if (formData.title.trim().length < 3) {
      newErrors.title = 'Task title must be at least 3 characters';
    }

    setErrors(newErrors);
    return !newErrors.title;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      await dispatch(
        createTask({
          boardId,
          title: formData.title.trim(),
          description: formData.description.trim() || undefined,
          status: formData.status,
          priority: formData.priority,
          type: formData.type,
          assigneeId: formData.assigneeId || undefined,
          epicId: formData.epicId || undefined,
          sprintId: defaultSprintId,
          dueDate: formData.dueDate || undefined,
          tags: formData.tags.length > 0 ? formData.tags : undefined,
          estimatedHours: formData.estimatedHours
            ? parseFloat(formData.estimatedHours)
            : undefined,
          storyPoints: formData.storyPoints
            ? parseInt(formData.storyPoints, 10)
            : undefined,
        })
      ).unwrap();

      dispatch(
        showToast({
          message: 'Task created successfully!',
          type: 'success',
        })
      );

      handleClose();
    } catch (error: any) {
      console.error('Task creation error:', error);

      // Parse backend validation errors
      const errorMessage = error.message || error.response?.data?.message || 'Failed to create task';

      // Check for specific validation errors
      const newErrors = {
        title: '',
        assigneeId: '',
        boardId: '',
        general: '',
      };

      if (errorMessage.includes('Board with ID')) {
        newErrors.boardId = errorMessage;
        newErrors.general = 'The board does not exist. Please refresh the page.';
      } else if (errorMessage.includes('User with ID') || errorMessage.includes('does not exist')) {
        newErrors.assigneeId = errorMessage;
      } else if (errorMessage.includes('Invalid board ID')) {
        newErrors.boardId = errorMessage;
      } else if (errorMessage.includes('Invalid assignee ID')) {
        newErrors.assigneeId = 'Please enter a valid user ID (numeric)';
      } else {
        newErrors.general = errorMessage;
      }

      setErrors(newErrors);

      // Show toast notification
      dispatch(
        showToast({
          message: newErrors.general || errorMessage,
          type: 'error',
        })
      );
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      status: defaultStatus,
      priority: TaskPriority.MEDIUM,
      type: TaskType.FEATURE,
      assigneeId: '',
      epicId: '',
      dueDate: '',
      estimatedHours: '',
      storyPoints: '',
      tags: [],
    });
    setTagInput('');
    setErrors({ title: '', assigneeId: '', boardId: '', general: '' });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Create New Task</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2 }}>
          {/* General Error Alert */}
          {errors.general && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrors(prev => ({ ...prev, general: '' }))}>
              {errors.general}
            </Alert>
          )}

          {/* Board Error Alert */}
          {errors.boardId && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrors(prev => ({ ...prev, boardId: '' }))}>
              {errors.boardId}
            </Alert>
          )}

          {/* Title */}
          <TextField
            fullWidth
            label="Task Title"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            error={!!errors.title}
            helperText={errors.title}
            placeholder="e.g., Implement user authentication"
            autoFocus
            required
            sx={{ mb: 2 }}
          />

          {/* Description */}
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            placeholder="Describe the task in detail..."
            multiline
            rows={4}
            sx={{ mb: 2 }}
          />

          {/* Row 1: Status, Priority, Type */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                label="Status"
              >
                <MenuItem value={TaskStatus.TODO}>To Do</MenuItem>
                <MenuItem value={TaskStatus.IN_PROGRESS}>In Progress</MenuItem>
                <MenuItem value={TaskStatus.REVIEW}>Review</MenuItem>
                <MenuItem value={TaskStatus.TESTING}>Testing</MenuItem>
                <MenuItem value={TaskStatus.DONE}>Done</MenuItem>
                <MenuItem value={TaskStatus.BLOCKED}>Blocked</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', e.target.value)}
                label="Priority"
              >
                <MenuItem value={TaskPriority.LOW}>Low</MenuItem>
                <MenuItem value={TaskPriority.MEDIUM}>Medium</MenuItem>
                <MenuItem value={TaskPriority.HIGH}>High</MenuItem>
                <MenuItem value={TaskPriority.URGENT}>Urgent</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                label="Type"
              >
                <MenuItem value={TaskType.FEATURE}>Feature</MenuItem>
                <MenuItem value={TaskType.BUG}>Bug</MenuItem>
                <MenuItem value={TaskType.QUALITY}>Quality</MenuItem>
                <MenuItem value={TaskType.INFRASTRUCTURE}>Infrastructure</MenuItem>
                <MenuItem value={TaskType.RESEARCH}>Research</MenuItem>
                <MenuItem value={TaskType.DOCUMENTATION}>Documentation</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Epic */}
          {epics.length > 0 && (
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Epic</InputLabel>
              <Select
                value={formData.epicId}
                onChange={(e) => handleInputChange('epicId', e.target.value)}
                label="Epic"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {epics.map((epic) => (
                  <MenuItem key={epic.id} value={epic.id}>
                    {epic.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Row 2: Due Date, Estimated Hours, Story Points */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="Due Date"
              type="date"
              value={formData.dueDate}
              onChange={(e) => handleInputChange('dueDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              fullWidth
              label="Estimated Hours"
              type="number"
              value={formData.estimatedHours}
              onChange={(e) => handleInputChange('estimatedHours', e.target.value)}
              placeholder="e.g., 8"
              inputProps={{ min: 0, step: 0.5 }}
            />

            <TextField
              fullWidth
              label="Story Points"
              type="number"
              value={formData.storyPoints}
              onChange={(e) => handleInputChange('storyPoints', e.target.value)}
              placeholder="e.g., 5"
              inputProps={{ min: 0, step: 1 }}
            />
          </Box>

          {/* Assignee */}
          <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.assigneeId}>
            <InputLabel>Assigned To</InputLabel>
            <Select
              value={formData.assigneeId}
              onChange={(e) => handleInputChange('assigneeId', e.target.value)}
              label="Assigned To"
              disabled={loadingMembers}
            >
              <MenuItem value="">
                <em>Unassigned</em>
              </MenuItem>
              {boardMembers.map((member) => (
                <MenuItem key={member.id} value={String(member.id)}>
                  <Box>
                    <Typography variant="body2">
                      {member.fullName || member.userName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {member.email} • {member.role}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {loadingMembers && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                Loading board members...
              </Typography>
            )}
            {!loadingMembers && boardMembers.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
                No board members found. Task will be unassigned.
              </Typography>
            )}
            {errors.assigneeId && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                {errors.assigneeId}
              </Typography>
            )}
          </FormControl>

          {/* Tags */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
              Tags
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                fullWidth
                size="small"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                placeholder="Add a tag..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
              />
              <Button variant="outlined" onClick={handleAddTag}>
                Add
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => handleRemoveTag(tag)}
                  size="small"
                />
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || !formData.title.trim()}
        >
          {loading ? <CircularProgress size={24} /> : 'Create Task'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateTaskModal;
